import memoizeOne from 'memoize-one';
import { defaultSchema } from '@atlaskit/adf-schema/schema-default';
import { getValidDocument } from '@atlaskit/editor-common/validator';
import { validateADFEntity, findAndTrackUnsupportedContentNodes } from '@atlaskit/editor-common/utils';
import { ACTION, ACTION_SUBJECT, EVENT_TYPE } from '@atlaskit/editor-common/analytics';
import { PLATFORM } from './analytics/events';
import { trackUnsupportedContentLevels } from './analytics/unsupported-content';
import { transformMediaLinkMarks } from '@atlaskit/adf-utils/transforms';
const SUPPORTS_HIRES_TIMER_API = !!(typeof window !== 'undefined' && window.performance && performance.now);
const withStopwatch = cb => {
  const startTime = SUPPORTS_HIRES_TIMER_API ? performance.now() : Date.now();
  const output = cb();
  const endTime = SUPPORTS_HIRES_TIMER_API ? performance.now() : Date.now();
  const time = endTime - startTime;
  return {
    output,
    time
  };
};
const _validation = (doc, schema, adfStage, useSpecBasedValidator, dispatchAnalyticsEvent) => {
  let result;
  if (useSpecBasedValidator) {
    // link mark on mediaSingle is deprecated, need to move link mark to child media node
    // https://product-fabric.atlassian.net/browse/ED-14043
    const {
      transformedAdf,
      isTransformed
    } = transformMediaLinkMarks(doc);
    if (isTransformed && dispatchAnalyticsEvent) {
      dispatchAnalyticsEvent({
        action: ACTION.MEDIA_LINK_TRANSFORMED,
        actionSubject: ACTION_SUBJECT.RENDERER,
        eventType: EVENT_TYPE.OPERATIONAL
      });
    }
    result = validateADFEntity(schema, transformedAdf || doc, dispatchAnalyticsEvent);
  } else {
    result = getValidDocument(doc, schema, adfStage);
  }
  if (!result) {
    return result;
  }

  // ProseMirror always require a child under doc
  if (result.type === 'doc' && useSpecBasedValidator) {
    if (Array.isArray(result.content) && result.content.length === 0) {
      result.content.push({
        type: 'paragraph',
        content: []
      });
    }

    // Just making sure doc is always valid
    if (!result.version) {
      result.version = 1;
    }
  }
  return result;
};
const memoValidation = memoizeOne(_validation, (newArgs, lastArgs) => {
  const [newDoc, newSchema, newADFStage, newUseSpecValidator] = newArgs;
  const [oldDoc, oldSchema, oldADFStage, oldUseSpecValidator] = lastArgs;

  // we're ignoring changes to dispatchAnalyticsEvent in this check
  const result = areDocsEqual(newDoc, oldDoc) && newSchema === oldSchema && newADFStage === oldADFStage && newUseSpecValidator === oldUseSpecValidator;
  return result;
});
const areDocsEqual = (docA, docB) => {
  if (docA === docB) {
    return true;
  }
  if (typeof docA === 'string' && typeof docB === 'string') {
    return docA === docB;
  }

  // PMNode
  if (docA.type && docA.toJSON && docB.type && docB.toJSON) {
    return JSON.stringify(docA.toJSON()) === JSON.stringify(docB.toJSON());
  }

  // Object
  return JSON.stringify(docA) === JSON.stringify(docB);
};
const _serializeFragment = (serializer, doc) => {
  return serializer.serializeFragment(doc.content);
};
const memoSerializeFragment = memoizeOne(_serializeFragment, (newArgs, lastArgs) => {
  const [newSerializer, newDoc] = newArgs;
  const [oldSerializer, oldDoc] = lastArgs;
  return newSerializer === oldSerializer && areDocsEqual(newDoc, oldDoc);
});
const _createNodeAndCheck = (schema, doc, dispatchAnalyticsEvent) => {
  const pmNode = schema.nodeFromJSON(doc);
  try {
    pmNode.check();
  } catch (err) {
    if (dispatchAnalyticsEvent) {
      dispatchAnalyticsEvent({
        action: ACTION.INVALID_PROSEMIRROR_DOCUMENT,
        actionSubject: ACTION_SUBJECT.RENDERER,
        attributes: {
          platform: PLATFORM.WEB,
          errorStack: err instanceof Error ? err.message : String(err)
        },
        eventType: EVENT_TYPE.OPERATIONAL
      });
    }
  }
  return pmNode;
};
const memoCreateNodeAndCheck = memoizeOne(_createNodeAndCheck, (newArgs, lastArgs) => {
  // ignore dispatchAnalyticsEvent
  const [newSchema, newDoc] = newArgs;
  const [oldSchema, oldDoc] = lastArgs;
  return newSchema === oldSchema && areDocsEqual(newDoc, oldDoc);
});
export const renderDocument = (doc, serializer, schema = defaultSchema, adfStage = 'final', useSpecBasedValidator = false, rendererId = 'noid', dispatchAnalyticsEvent, unsupportedContentLevelsTracking, appearance) => {
  const stat = {
    sanitizeTime: 0
  };
  const {
    output: validDoc,
    time: sanitizeTime
  } = withStopwatch(() => {
    return memoValidation(doc, schema, adfStage, useSpecBasedValidator, dispatchAnalyticsEvent);
  });

  // save sanitize time to stats
  stat.sanitizeTime = sanitizeTime;
  if (!validDoc) {
    return {
      stat,
      result: null
    };
  }
  const {
    output: node,
    time: buildTreeTime
  } = withStopwatch(() => {
    return memoCreateNodeAndCheck(schema, validDoc, dispatchAnalyticsEvent);
  });

  // save build tree time to stats
  stat.buildTreeTime = buildTreeTime;
  const {
    output: result,
    time: serializeTime
  } = withStopwatch(() => {
    return memoSerializeFragment(serializer, node);
  });

  // save serialize tree time to stats
  stat.serializeTime = serializeTime;
  if (dispatchAnalyticsEvent && useSpecBasedValidator) {
    findAndTrackUnsupportedContentNodes(node, schema, dispatchAnalyticsEvent);
    if (unsupportedContentLevelsTracking !== null && unsupportedContentLevelsTracking !== void 0 && unsupportedContentLevelsTracking.enabled) {
      const documentData = {
        doc: validDoc,
        appearance,
        rendererId
      };
      trackUnsupportedContentLevels(documentData, unsupportedContentLevelsTracking, dispatchAnalyticsEvent);
    }
  }
  return {
    result,
    stat,
    pmDoc: node
  };
};